<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="6">
        <v-select
          :items="softwares"
          v-model="selectedSoftware"
          :loading="loadingSoftwares"
          label="Software"
          item-title="name"
          item-value="id"
          @update:model-value="onSoftwareChange"
        />
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-btn
          color="primary"
          @click="openUploadDialog"
          :disabled="!selectedSoftware"
        >
          Nova Versão
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="versions"
          :loading="loadingVersions"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          :items-length="totalItems"
          :items-per-page-options="[
            { value: 10, title: '10' },
            { value: 20, title: '20' },
            { value: 30, title: '30' },
            { value: 40, title: '40' },
            { value: 50, title: '50' },
          ]"
        >
          <template v-slot:item.size_bytes="{ item }">
            {{ formatFileSize(item.size_bytes) }}
          </template>
          <template v-slot:item.sha256_hash="{ item }">
            <code class="text-caption">{{ item.sha256_hash }}</code>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template v-slot:item.is_active="{ item }">
            <v-chip
              :color="item.is_active ? 'success' : 'default'"
              size="small"
            >
              {{ item.is_active ? "Ativo" : "Inativo" }}
            </v-chip>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialog" max-width="600" :persistent="uploading">
      <v-card>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>Nova Versão</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-form ref="uploadFormRef" @submit.prevent="onSubmitUpload">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :model-value="getSelectedSoftwareName()"
                  label="Software"
                  readonly
                  disabled
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="uploadForm.version_code"
                  :rules="[
                    validationRules.required,
                    validationRules.numeric,
                    validationRules.min(1),
                  ]"
                  label="Código da Versão"
                  name="version_code"
                  type="number"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="uploadForm.version_name"
                  :rules="[validationRules.required]"
                  label="Nome da Versão"
                  name="version_name"
                />
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="uploadForm.file"
                  :rules="[validationRules.required]"
                  label="Arquivo do Software"
                  name="file"
                  show-size
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            variant="text"
            @click="onCloseUploadDialog"
            :disabled="uploading"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="onSubmitUpload"
            :loading="uploading"
            :disabled="!uploadForm.file"
          >
            Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import {
  useSoftwaresQuery,
  useSoftwareVersionsQuery,
  useUploadVersionMutation,
} from "@/queries/modules/software";
import { validationRules } from "@/utils/validation";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSnackbar } from "@/plugins/snackbar";

const $snackbar = useSnackbar();

const headers = [
  { title: "Data de Criação", key: "created_at" },
  { title: "Nome da Versão", key: "version_name" },
  { title: "Código da Versão", key: "version_code" },
  { title: "Tamanho do Arquivo", key: "size_bytes" },
  { title: "Hash SHA256", key: "sha256_hash" },
  { title: "Status", key: "is_active" },
];

const uploadFormRef = ref(null);
const selectedSoftware = ref(null);
const uploadDialog = ref(false);
const page = ref(1);
const itemsPerPage = ref(10);
const uploadForm = ref({
  version_code: "",
  version_name: "",
  file: null,
});

// TanStack Query hooks
const softwaresQuery = useSoftwaresQuery();
const softwares = computed(() => softwaresQuery.data.value || []);
const loadingSoftwares = computed(() => softwaresQuery.isLoading.value);

const versionParams = computed(() => ({
  software_id: selectedSoftware.value,
  page: page.value,
  per_page: itemsPerPage.value,
}));
const versionsQuery = useSoftwareVersionsQuery(versionParams);
const versions = computed(() => versionsQuery.data.value || []);
const loadingVersions = computed(() => versionsQuery.isLoading.value);
const totalItems = computed(() => versions.value.length);

const uploadVersionMutation = useUploadVersionMutation();
const uploading = computed(() => uploadVersionMutation.isPending.value);

// Auto-select first software when list loads
watch(
  softwares,
  (newSoftwares) => {
    if (newSoftwares.length > 0 && !selectedSoftware.value) {
      selectedSoftware.value = newSoftwares[0].id;
    }
  },
  { immediate: true },
);

const onSoftwareChange = () => {
  page.value = 1;
  // Query will auto-refetch due to reactive params
};

const getSelectedSoftwareName = () => {
  const software = softwares.value.find((s) => s.id === selectedSoftware.value);
  return software ? software.name : "";
};

const openUploadDialog = () => {
  prefillUploadForm();
  uploadDialog.value = true;
};

const prefillUploadForm = () => {
  if (versions.value.length > 0) {
    // Find the most recent version (highest version_code)
    const latestVersion = versions.value.reduce((latest, current) => {
      return current.version_code > latest.version_code ? current : latest;
    });

    // Prefill with incremented version_code and same version_name
    uploadForm.value.version_code = latestVersion.version_code + 1;
    uploadForm.value.version_name = latestVersion.version_name;
  } else {
    // If no versions exist, start with defaults
    uploadForm.value.version_code = 1;
    uploadForm.value.version_name = "";
  }
};

const onSubmitUpload = async () => {
  const { valid } = await uploadFormRef.value.validate();
  if (!valid) return;

  try {
    await uploadVersionMutation.mutateAsync({
      software_id: selectedSoftware.value,
      version_code: uploadForm.value.version_code,
      version_name: uploadForm.value.version_name,
      file: uploadForm.value.file,
    });

    $snackbar.success("Versão enviada com sucesso");
    uploadDialog.value = false;
    resetUploadForm();
  } catch (e) {
    $snackbar.error(`Erro ao enviar versão: ${e.message}`);
  }
};

const onCloseUploadDialog = () => {
  uploadDialog.value = false;
  resetUploadForm();
};

const resetUploadForm = () => {
  uploadForm.value = {
    version_code: "",
    version_name: "",
    file: null,
  };
  uploadFormRef.value?.resetValidation();
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const formatDate = (dateString) => {
  return format(parseISO(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
};

// Queries auto-refetch when reactive params change
</script>
